import React, { useEffect, useState } from 'react';
import Table from 'react-bootstrap/Table';
import { useNavigate } from "react-router-dom";
import { Base64 } from 'js-base64';
import { Avatar } from '@mui/material';
import axios from 'axios';
import { dateFormat } from "../../utils";
import Swal from '../../messageBox/customSweetAlert';
import { UPDATE_DRIVER_STATUS } from '../../constant';

/** Map GET /drivers item to the row shape used by this table (legacy + v1 API). */
export function mapDriverApiToTableRow(item) {
    if (!item) return item;
    if (item.driver_id != null && item.driver_details != null) {
        return item;
    }

    const driverId = item.driver?.driver_id ?? item.user_id;
    const fullName = [item.first_name, item.middle_name, item.last_name]
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();

    return {
        driver_id: driverId,
        first_name: item.first_name,
        last_name: item.last_name,
        middle_name: item.middle_name,
        full_name: fullName,
        driver_name: fullName,
        umpi_no: item.driver?.umpi_no ?? item.umpi_no,
        lisence_expiry_date: item.driver?.lisence_expiry_date ?? item.lisence_expiry_date,
        is_active: item.is_active,
        driver_details: {
            ...(item.driver_details || {}),
            user_role: item.driver_details?.user_role ?? item.user_role,
            mobile_no: item.mobile_no ?? item.driver_details?.mobile_no,
            is_active: item.is_active ?? item.driver_details?.is_active,
        },
    };
}

function DriverTable({ tableData, startIndex = 0 }) {
    const navigate = useNavigate();
    const [rows, setRows] = useState(tableData || []);
    const [updatingDriverIds, setUpdatingDriverIds] = useState(new Set());

    useEffect(() => {
        const list = tableData || [];
        setRows(list.map((row) => mapDriverApiToTableRow(row)));
    }, [tableData]);

    const handleEditClick = (id) => {
        navigate(`/home/driver/edit/${id}`);
    };

    const isDriverActive = (details) =>
        details?.driver_details?.is_active === 1 || details?.is_active === 1;

    const updateDriverStatus = async (driverId, isActive) => {
        if (updatingDriverIds.has(driverId)) return;

        setUpdatingDriverIds((prev) => new Set(prev).add(driverId));

        try {
            const response = await axios.patch(
                `${UPDATE_DRIVER_STATUS}/${driverId}`,
                { isActive },
                { headers: { 'Content-Type': 'application/json' } },
            );

            const isOk =
                response?.data?.success === true ||
                response?.data?.code === 200 ||
                response?.status === 200;

            if (isOk) {
                setRows((prev) =>
                    prev.map((row) =>
                        row.driver_id === driverId
                            ? {
                                ...row,
                                is_active: isActive,
                                driver_details: {
                                    ...row.driver_details,
                                    is_active: isActive,
                                },
                            }
                            : row,
                    ),
                );
                Swal.fire({
                    title: 'Success!',
                    text: 'Successfully updated',
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
            } else {
                Swal.fire({
                    title: 'Failed!',
                    text: response?.data?.message || 'Failed to update driver status.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Error!',
                text:
                    error?.response?.data?.message ||
                    error?.message ||
                    'Failed to update driver status.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setUpdatingDriverIds((prev) => {
                const next = new Set(prev);
                next.delete(driverId);
                return next;
            });
        }
    };

    const handleStatusToggle = async (details) => {
        const driverId = details?.driver_id;
        if (!driverId || updatingDriverIds.has(driverId)) return;

        const currentlyActive = isDriverActive(details);

        if (currentlyActive) {
            const result = await Swal.fire({
                text: 'Are you sure want to deactive this driver',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
            });

            if (!result.isConfirmed) return;

            await updateDriverStatus(driverId, 0);
            return;
        }

        await updateDriverStatus(driverId, 1);
    };

    return (
        <Table striped bordered hover className='tbl-require'>
            <thead>
                <tr>
                    <th>#</th>
                    <th>Driver Name</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Cell Phone</th>
                    <th>UMPI No.</th>
                    {/* <th>User Name</th> */}
                    <th>Expiry of DL</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                </tr>
            </thead>
            <tbody>
                {rows && rows.length > 0 ? (
                    rows.map((details, index) => (
                        <tr key={details.driver_id ?? index}>
                            <td>{startIndex + index + 1}</td>
                            <td>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar alt={details.driver_name} style={{ marginRight: '10px' }} />
                                    <a href={details.driver_details?.user_role == 2 ? `/home/profile/myProfile` : `/home/driver/profile/id?profilId=${Base64.encode(details.driver_id)}`}
                                        target="_blank"
                                        onClick={() => localStorage.setItem('activeMenu', 'Driver List')}
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none', color: 'rgb(49, 135, 111)' }}
                                    >
                                        {details.driver_name ? details.driver_name : details.full_name}
                                    </a>
                                </div>

                            </td>
                            <td>{details.driver_details?.user_role != 2 ? details.first_name : <span style={{ color: 'grey' }}>N/A</span>}</td>
                            <td>{details.driver_details?.user_role != 2 ? details.last_name : <span style={{ color: 'grey' }}>N/A</span>}</td>
                            <td>{details.driver_details?.user_role != 2 ? details?.driver_details?.mobile_no : <span style={{ color: 'grey' }}>N/A</span>}</td>
                            <td>{details.driver_details?.user_role != 2 ? details?.umpi_no : <span style={{ color: 'grey' }}>N/A</span>}</td>
                            {/* <td>{details?.login_user_name}</td> */}
                            <td>{details.driver_details?.user_role != 2 ? dateFormat(details?.lisence_expiry_date) : <span style={{ color: 'grey' }}>N/A</span>}</td>
                            <td>{isDriverActive(details) ? <span style={{ color: 'green' }}>Active</span> : <span style={{ color: 'red' }}>Inactive</span>}</td>
                            <td style={{ textAlign: 'center', cursor: 'pointer' }}>
                                {
                                    details.driver_details?.user_role != 2 ?
                                        <>
                                            <div
                                                className="form-check form-switch d-inline-flex align-items-center justify-content-center me-2"
                                                title={isDriverActive(details) ? 'Active' : 'Inactive'}
                                            >
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    checked={isDriverActive(details)}
                                                    disabled={updatingDriverIds.has(details.driver_id)}
                                                    onChange={() => handleStatusToggle(details)}
                                                />
                                            </div>
                                            <i className="pi pi-pen-to-square icon-btn-edit" onClick={() => handleEditClick(details.driver_id)}></i>
                                        </> : ''
                                }
                            </td>
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan="8" style={{ textAlign: 'center' }}>No records found</td>
                    </tr>
                )}
            </tbody>
        </Table>
    )
}

export default DriverTable
